import DataTableCompanyEmployees from "@/components/data-table-company-employees/data-table-company-employees";
import PageSkeleton from "@/components/page-skeleton";
import {
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useTranslation } from "@/hooks/useTranslation";
import { setWindowTitle } from "@/lib/window-title";
import { EmployeeAssignationDTO, EmployeeDTO } from "@shaarp/common";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

/**
 * A team whose contact switch is off sees no key contacts (#757): its assigned
 * contacts are not fetched, and the page sends it to its assigned companies.
 */
export default function ListingAssignationsEmployeesPage() {
  const arePeopleVisible = useArePeopleVisible();
  if (!arePeopleVisible) {
    return <Navigate to="/listing/assignations/companies" replace />;
  }
  return <AssignedEmployees />;
}

function AssignedEmployees() {
  const api = useApi();
  const { t } = useTranslation();
  const toastError = useToastError();

  const [employees, setEmployees] = useState<EmployeeDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get<EmployeeAssignationDTO[]>(
        `/listing/assignations/employees`,
      );
      setEmployees(data.map((a) => a.employee));
    } catch {
      toastError();
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setWindowTitle(t("listing.page.assignedEmployees.title"));
    fetchData();
  }, []);

  return (
    <div className="absolute top-0 left-0 flex-1 w-full h-[calc(100%)] overflow-auto">
      <ResizablePanelGroup orientation="horizontal" className="w-full">
        <ResizablePanel className="relative">
          <div className="overflow-auto absolute top-0 left-0 flex-1 p-8 pt-6 compact:p-6 compact:pt-4 space-y-4 w-full h-full">
            <div className="flex-1 space-y-5">
              {isLoading && <PageSkeleton />}
              {!isLoading && (
                <>
                  <div className="space-y-2">
                    <h2 className="text-2xl font-bold tracking-tight">
                      {t("listing.page.assignedEmployees.title")}
                    </h2>
                  </div>
                  <DataTableCompanyEmployees employees={employees} />
                </>
              )}
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
